import React, { useState } from 'react';
import { TestTube, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { pocketBaseUrl, speedcheckService } from '../services/pocketbase';

const ConnectionTest = ({ onConnectionSuccess }) => {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const runConnectionTest = async () => {
    setTesting(true);
    setResult(null);
    setError(null);

    try {
      console.log('Starting connection test...');
      
      // Test basic connection
      await speedcheckService.testConnection();

      const records = await speedcheckService.getAllRecords();
      
      setResult({
        success: true,
        message: 'Connection successful!',
        recordCount: records.length,
        sampleFields: records.length > 0 ? Object.keys(records[0]) : []
      });
      
      if (onConnectionSuccess) {
        onConnectionSuccess();
      }
      
    } catch (err) {
      console.error('Connection test failed:', err);
      setError({
        message: err.message,
        details: err.status || err.statusText || 'Unknown error',
        stack: err.stack
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="card p-6 max-w-2xl mx-auto">
      <div className="flex items-center space-x-3 mb-6">
        <TestTube className="w-6 h-6 text-blue-600" />
        <h2 className="text-xl font-semibold text-gray-900">PocketBase Connection Test</h2>
      </div>

      <div className="space-y-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-blue-900 mb-2">Configuration:</h3>
          <div className="text-sm text-blue-800 space-y-1">
            <div>URL: {pocketBaseUrl}</div>
            <div>Collection: speedchecks</div>
          </div>
        </div>

        <button
          onClick={runConnectionTest}
          disabled={testing}
          className="btn-primary flex items-center space-x-2"
        >
          {testing ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Testing Connection...</span>
            </>
          ) : (
            <>
              <TestTube className="w-4 h-4" />
              <span>Run Connection Test</span>
            </>
          )}
        </button>

        {result && (
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-900">{result.message}</span>
            </div>
            <div className="text-sm text-green-800 space-y-1">
              <div>• Records found: {result.recordCount}</div>
              <div>• Sample fields: {result.sampleFields.join(', ')}</div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <XCircle className="w-5 h-5 text-red-600" />
              <span className="text-sm font-medium text-red-900">Connection Failed</span>
            </div>
            <div className="text-sm text-red-800 space-y-1">
              <div>• Error: {error.message}</div>
              <div>• Details: {error.details}</div>
              {error.stack && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs">Show Stack Trace</summary>
                  <pre className="text-xs mt-1 bg-red-100 p-2 rounded overflow-auto">
                    {error.stack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        )}

        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-900">Troubleshooting Tips</span>
          </div>
          <ul className="text-sm text-yellow-800 space-y-1">
            <li>Start the local server with npm run pocketbase</li>
            <li>Confirm the URL above matches the running PocketBase address</li>
            <li>Confirm the speedchecks collection exists</li>
            <li>Open the browser console for the request error</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ConnectionTest;
